import { useMemo, useState } from 'react';
import { STATUSES } from '../../types/board';
import type { Board as BoardT, Item, Status } from '../../types/board';
import ItemCard from './ItemCard';
import { fuzzyMatch } from '../../lib/fuzzyMatch';

export default function Board({
  board,
  activeCategoryId,
  search,
  onOpenItem,
  onDropStatus,
  onAddItem,
}: {
  board: BoardT;
  activeCategoryId: string | null;
  search: string;
  onOpenItem: (id: string) => void;
  onDropStatus: (itemId: string, status: Status) => void;
  onAddItem: (status: Status) => void;
}) {
  const [dragOverStatus, setDragOverStatus] = useState<Status | null>(null);

  const filtered = useMemo(() => {
    return board.items.filter((i) => {
      if (activeCategoryId && !i.categoryIds.includes(activeCategoryId)) return false;
      if (!search.trim()) return true;
      return fuzzyMatch(search, i.title) || fuzzyMatch(search, i.vendor) || fuzzyMatch(search, i.notes);
    });
  }, [board.items, activeCategoryId, search]);

  const grouped = (status: Status) => filtered.filter((i) => i.status === status);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-[18px] items-start">
      {STATUSES.map((s) => {
        const isDragOver = dragOverStatus === s.id;
        const items: Item[] = grouped(s.id);
        return (
          <div
            key={s.id}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={() => setDragOverStatus(s.id)}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node)) return;
              setDragOverStatus((cur) => (cur === s.id ? null : cur));
            }}
            onDrop={(e) => {
              e.preventDefault();
              const itemId = e.dataTransfer.getData('text/plain');
              if (itemId) onDropStatus(itemId, s.id);
              setDragOverStatus(null);
            }}
            className="rounded-[10px] p-2 -m-2 min-h-[200px]"
            style={{
              background: isDragOver ? 'oklch(94% 0.03 20)' : 'transparent',
              boxShadow: isDragOver ? 'inset 0 0 0 2.5px oklch(50% 0.1 20)' : 'none',
            }}
          >
            <div
              className="flex items-center justify-between pb-2.5 mb-3 border-b-2"
              style={{ borderColor: s.accent }}
            >
              <span className="text-xs font-bold uppercase tracking-wider text-muted">{s.label}</span>
              <span className="text-[11px] text-muted bg-[oklch(92%_0.012_60)] rounded-full px-2 py-0.5">
                {items.length}
              </span>
            </div>
            <div className="flex flex-col gap-2.5">
              {items.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  board={board}
                  onOpen={() => onOpenItem(item.id)}
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', item.id)}
                />
              ))}
              <button
                onClick={() => onAddItem(s.id)}
                className="rounded-lg border-[1.5px] border-dashed border-line text-xs text-muted p-2.5 hover:border-terra hover:text-terra"
              >
                + Add item
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
